import { motion } from "framer-motion";
import { Rocket, Sparkles, Brain, Building2 } from "lucide-react";

const stops = [
  {
    tag: "Next month",
    title: "Launch on iOS &amp; Android",
    body: "The public launch, on both stores, in your pocket.",
    icon: Rocket,
    color: "var(--accent)",
    bg: "var(--accent-soft)",
    active: true,
  },
  {
    tag: "Next quarter",
    title: "Contact management, effortless",
    body: "Faster imports, smarter sub-groups, deeper phone integrations.",
    icon: Sparkles,
    color: "var(--blue)",
    bg: "var(--blue-soft)",
  },
  {
    tag: "In six months",
    title: "Your intelligent relationship assistant",
    body: "A voice-first assistant that captures every call, drafts your notes, and schedules the next touch — automatically.",
    icon: Brain,
    color: "var(--purple)",
    bg: "var(--purple-soft)",
  },
  {
    tag: "Within a year",
    title: "The Relationship OS for small businesses",
    body: "The one place every deal, every contact, every follow-up lives — for teams, not just individuals.",
    icon: Building2,
    color: "#0F766E",
    bg: "#D1FAE5",
  },
];

const scrollTo = (id) => {
  const el = document.querySelector(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Roadmap() {
  return (
    <section id="roadmap" className="relative py-24 md:py-32 border-t border-black/5" data-testid="roadmap-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-8">
            <div className="overline mb-4">Where this is going</div>
            <h2 className="font-display text-4xl md:text-6xl font-medium leading-[0.95] tracking-tight text-[color:var(--ink)]">
              You&apos;re joining early — here&apos;s
              <span className="gradient-text" style={{ fontFamily: "var(--font-serif)", fontStyle: "italic" }}>
                {" "}
                what happens next.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4 text-[color:var(--ink-soft)] text-base md:text-lg">
            The launch is the beginning. Every waitlist member gets a vote on what we build after.
            <button
              onClick={() => scrollTo("#waitlist")}
              data-testid="roadmap-cta"
              className="mt-3 block text-[color:var(--accent)] link-underline text-sm font-medium"
            >
              Join &amp; shape the roadmap →
            </button>
          </div>
        </div>

        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-6 top-2 bottom-2 w-px bg-[color:var(--line-strong)] md:left-1/2" />

          <div className="space-y-10">
            {stops.map((s, i) => (
              <motion.div
                key={s.tag}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className={`relative grid md:grid-cols-2 gap-6 md:gap-10 items-center`}
                data-testid={`roadmap-stop-${i}`}
              >
                <div className={`${i % 2 === 0 ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"}`}>
                  <div className="rounded-3xl border border-[color:var(--line)] bg-white p-6 md:p-8 shadow-sm relative">
                    {s.active && (
                      <span className="absolute -top-3 left-6 md:left-auto md:right-6 chip !text-[10px]"
                            style={{ background: "var(--ink)", color: "white", borderColor: "transparent" }}>
                        Up next
                      </span>
                    )}
                    <div className="flex items-center gap-3 mb-4 md:justify-start"
                         style={{ justifyContent: i % 2 === 0 ? "flex-end" : "flex-start" }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.bg }}>
                        <s.icon className="w-4 h-4" style={{ color: s.color }} strokeWidth={1.8} />
                      </div>
                      <span className="overline" style={{ color: s.color }}>{s.tag}</span>
                    </div>
                    <h3
                      className="font-display text-2xl md:text-3xl font-medium leading-tight text-[color:var(--ink)]"
                      dangerouslySetInnerHTML={{ __html: s.title }}
                    />
                    <p
                      className="mt-3 text-[color:var(--ink-soft)] text-sm md:text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: s.body }}
                    />
                  </div>
                </div>

                {/* Timeline dot */}
                <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-8 md:top-1/2 md:-translate-y-1/2">
                  <div className="w-4 h-4 rounded-full bg-white border-2 shadow-sm" style={{ borderColor: s.color }}>
                    {s.active && (
                      <span className="block w-full h-full rounded-full animate-ping"
                            style={{ background: s.color, opacity: 0.4 }} />
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
